import { useQuery } from '@tanstack/react-query';

import { useApiClient } from '../ApiClientProvider';
import { queryKeys } from '../keys';
import type {
  PackageTemplate,
  PackageTemplatesByCountry,
  ParcelLimits,
} from '../types/package-templates';

export function usePackageTemplates() {
  const api = useApiClient();
  return useQuery<PackageTemplatesByCountry[], Error>({
    queryKey: queryKeys.packageTemplates,
    queryFn: async ({ signal }) => {
      const response = await api.get<{ data: PackageTemplatesByCountry[] }>(
        '/admin/package-templates',
        signal,
      );
      return response.data;
    },
  });
}

/**
 * Predefined sizes offered for the given destinations, deduplicated by slug in
 * first-seen order (when the selected orders span markets whose slugs differ in
 * dimensions, the first market's numbers label the option - the backend still
 * resolves each shipment against its own destination). Pass `undefined` to get
 * the union across every market (the account-level default select); a list
 * that holds no resolvable country yields nothing on purpose - the backend
 * offers no predefined size for an unknown destination either.
 */
export function predefinedTemplatesFor(
  groups: PackageTemplatesByCountry[] | undefined,
  countries?: readonly (string | null | undefined)[],
): PackageTemplate[] {
  if (!groups) return [];
  const wanted = countries
    ? new Set(countries.filter((c): c is string => Boolean(c)).map((c) => c.toUpperCase()))
    : null;
  const seen = new Set<string>();
  const result: PackageTemplate[] = [];
  for (const group of groups) {
    if (wanted && !wanted.has(group.country.toUpperCase())) continue;
    for (const template of group.templates) {
      if (seen.has(template.key)) continue;
      seen.add(template.key);
      result.push(template);
    }
  }
  return result;
}

/**
 * Whether a package fits a delivery service's limits (cm / kg on both sides -
 * predefined and custom packages are stored in those units). Null limits mean
 * the service enforces none. Dimensions compare as a sorted bounding box, so
 * axis order is irrelevant - mirrors the backend `ParcelLimits::fits`.
 */
export function packageFitsLimits(
  pkg: Pick<PackageTemplate, 'length' | 'width' | 'height' | 'weight'>,
  limits: ParcelLimits | null | undefined,
): boolean {
  if (!limits) return true;
  if (pkg.weight > limits.max_weight_kg) return false;
  const dims = [pkg.length, pkg.width, pkg.height].sort((a, b) => a - b);
  const caps = [limits.length, limits.width, limits.height].sort((a, b) => a - b);
  return dims.every((dim, index) => dim <= caps[index]);
}
